import { useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";
import { responsive } from "@/lib/media";

const TAP_PX = 6;

export function BeforeAfter({ before, after, alt, fit = "cover", className = "", imgClassName = "", onTap, onLoad }) {
  const [pos, setPos] = useState(50);
  const box = useRef(null);
  const drag = useRef(null);

  const posFromEvent = (e) => {
    const r = box.current.getBoundingClientRect();
    return Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
  };
  const down = (e) => {
    drag.current = { x: e.clientX, moved: false, start: pos };
    box.current.setPointerCapture(e.pointerId);
  };
  const move = (e) => {
    if (!drag.current) return;
    if (Math.abs(e.clientX - drag.current.x) > TAP_PX) drag.current.moved = true;
    if (drag.current.moved) setPos(posFromEvent(e));
  };
  const up = () => {
    if (drag.current && !drag.current.moved && onTap) onTap();
    drag.current = null;
  };
  const key = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 5));
    else if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 5));
    else if ((e.key === "Enter" || e.key === " ") && onTap) { e.preventDefault(); onTap(); }
  };
  const stop = (e) => e.stopPropagation();
  const imgCls = `${fit === "contain" ? "object-contain" : "object-cover"} ${imgClassName}`;

  return (
    <div
      ref={box} role="slider" tabIndex={0} aria-label="Drag to compare before and after" aria-valuenow={Math.round(pos)} aria-valuemin={0} aria-valuemax={100}
      className={`relative select-none cursor-ew-resize touch-pan-y ${className}`}
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onKeyDown={key}
      onTouchStart={stop} onTouchEnd={stop} onClick={stop}
      data-testid="before-after"
    >
      <img {...responsive(after)} alt={alt} draggable={false} loading="lazy" decoding="async" onLoad={onLoad} className={`w-full h-full ${imgCls}`} data-testid="before-after-after" />
      <img {...responsive(before)} alt={`Before — ${alt}`} draggable={false} loading="lazy" decoding="async" className={`absolute inset-0 w-full h-full ${imgCls}`}
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} data-testid="before-after-before" />
      <div className="absolute inset-y-0 w-0.5 bg-white/90 shadow-[0_0_12px_rgba(0,0,0,0.6)] pointer-events-none" style={{ left: `calc(${pos}% - 1px)` }} />
      <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-white text-slate-900 shadow-lg flex items-center justify-center pointer-events-none"
        style={{ left: `${pos}%` }} data-testid="before-after-handle">
        <ChevronsLeftRight className="w-5 h-5" />
      </div>
      <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-mono uppercase tracking-widest pointer-events-none">Before</span>
      <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-[#FFD600] text-slate-900 text-[10px] font-mono uppercase tracking-widest pointer-events-none">After</span>
    </div>
  );
}
